/**
 * calculateEmployeeStats
 *
 * Calculates capacity statistics for an employee based on their bookings
 * and the visible date range.
 *
 * Bug #9011: Capacity now excludes Norwegian public holidays (not just weekends)
 * Feature: Capacity settings allow including holidays/weekends in calculation
 *
 * @example
 * ```ts
 * const stats = calculateEmployeeStats(
 *   employee,
 *   bookingsByEmployee,
 *   visibleDays,
 *   8, // hours per day
 *   { includePublicHolidays: false, includeWeekends: false }
 * );
 * // { totalHours: 32, totalCapacity: 40, percentage: 80, isOverbooked: false, isWarning: true }
 * ```
 */

import type { IBooking, IServiceEmployee } from "@/services/types";
import { calculateTotalHours } from "@/utils/booking";
import { isPublicHoliday } from "@/utils/date/holidays";
import type { EmployeeStats } from "./types";
import { isSameDay, isWeekend } from "date-fns";

/**
 * Options for capacity calculation
 */
export interface CapacityOptions {
  /** If true, public holidays count as working days in capacity */
  includePublicHolidays: boolean;
  /** If true, weekends add to capacity */
  includeWeekends: boolean;
}

/**
 * Default capacity options (excludes holidays and weekends)
 */
export const DEFAULT_CAPACITY_OPTIONS: CapacityOptions = {
  includePublicHolidays: false,
  includeWeekends: false,
};

/**
 * Calculate employee capacity statistics
 *
 * @param employee - The employee to calculate stats for
 * @param bookingsByEmployee - Map of employee IDs to their bookings
 * @param visibleDays - Array of visible dates in the current view
 * @param hoursInDay - Standard working hours per day (e.g., 7.5 or 8)
 * @param options - Capacity calculation options (optional, defaults to exclude holidays/weekends)
 * @returns Statistics including total hours, capacity, percentage, and flags
 */
export function calculateEmployeeStats(
  employee: IServiceEmployee,
  bookingsByEmployee: Map<number, IBooking[]>,
  visibleDays: Date[],
  hoursInDay: number,
  options: CapacityOptions = DEFAULT_CAPACITY_OPTIONS,
): EmployeeStats {
  const empBookings = bookingsByEmployee.get(employee.id) || [];
  const visibleBookings = empBookings.filter((b) =>
    visibleDays.some((day) => isSameDay(b.date, day)),
  );
  const totalHours = calculateTotalHours(visibleBookings);

  // Calculate working days based on capacity settings
  // Bug #9011: Norwegian public holidays excluded from capacity (unless overridden)
  const workingDayCount = visibleDays.filter((day) => {
    const weekend = isWeekend(day);
    const holiday = isPublicHoliday(day);

    // If it's a weekend
    if (weekend) {
      // Include only if weekends are enabled in capacity settings
      return options.includeWeekends;
    }

    // If it's a public holiday (not weekend)
    if (holiday) {
      // Include only if public holidays are enabled in capacity settings
      return options.includePublicHolidays;
    }

    // Regular weekday - always include
    return true;
  }).length;

  const totalCapacity = workingDayCount * hoursInDay;
  const percentage = totalCapacity > 0 ? (totalHours / totalCapacity) * 100 : 0;

  return {
    totalHours,
    totalCapacity,
    percentage: Math.min(percentage, 100),
    isOverbooked: percentage > 100,
    isWarning: percentage > 80 && percentage <= 100,
  };
}
